<article class="content-container">
  @defer (when site() && report()) {
    <header class="no-print">
      <app-toolbar>
        <div class="left">Report for {{ this.startOfPreviousMonth | date: 'MMMM yyyy' }}</div>
        <div class="right">
          <app-button (click)="showDebug()" label="Enable audit" icon="mdi mdi-code-json" />
          <app-button (click)="printPage()" label="Print Report" icon="mdi mdi-printer" />
        </div>
      </app-toolbar>
    </header>
    <main>
      <section class="report-header">
        <img src="https://4d-org-logos.s3.eu-west-2.amazonaws.com/{{ org.settings.theme_logo }}" alt="logo" />
      </section>
      <section class="report-1">
        <h1>Monthly Footfall</h1>
        <table class="table">
          <thead>
            <tr>
              <th>Site</th>
              <th class="right">{{ report().report_1.month_current_name }}</th>
              <th class="right">{{ report().report_1.month_current_last_year_name }}</th>
              <th class="right">% vs {{ report().report_1.month_current_last_year_name }}</th>
              <th class="right">{{ report().report_1.month_previous_name }}</th>
              <th class="right">% vs {{ report().report_1.month_previous_name }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                {{ site().title }}
              </td>
              <td class="right">{{ report().report_1.month_current_total | number: '1.0-0' }}</td>
              <td class="right">{{ report().report_1.month_last_year_total | number: '1.0-0' }}</td>
              <td class="right">{{ report().report_1.pct_vs_last_year }}%</td>
              <td class="right">{{ report().report_1.month_previous_total | number: '1.0-0' }}</td>
              <td class="right">{{ report().report_1.pct_vs_previous_month }}%</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- -->

      <section class="report-2">
        <h1>Headlines</h1>
        <p>
          Footfall for the year to date is <span>{{ report().report_2.ytd_total | number: '1.0-0' }}</span
          >, which was

          @if (report().report_2.ytd_total_last_year > report().report_2.ytd_total) {
            a decrease of {{ report().report_2.ytd_pct_vs_last_year ?? 0 }}%
          } @else {
            an increase of {{ report().report_2.ytd_pct_vs_last_year ?? 0 }}%
          }

          on the same month last year.
        </p>

        <p>
          The number of visitors counted for the month of {{ report().report_1.month_current_name }} was <span>{{ report().report_1.month_current_total | number: '1.0-0' }}</span
          >, which was

          @if (report().report_1.month_current_total < report().report_1.month_previous_total) {
            a decrease of {{ report().report_1.pct_vs_last_year ?? 0 }}%
          } @else {
            an increase of {{ report().report_1.pct_vs_last_year ?? 0 }}%
          }

          on the same month last year.
        </p>

        <p>
          The busiest day this month was {{ report().report_2.busiest_day_formatted }} with a total of <span>{{ report().report_2.busiest_day_total_footfall | number: '1.0-0' }}</span> visitors.
        </p>

        <p>
          The busiest hour this month was {{ report().report_2.busiest_hour_readable }} with a total of <span>{{ report().report_2.busiest_hour_total_footfall | number: '1.0-0' }}</span> visitors.
        </p>
      </section>
      <!-- -->
      <section class="pagebreakafter">
        <h1>Monthly Footfall counts by location</h1>

        <table class="table">
          <thead>
            <tr>
              <th>Location</th>
              <th class="text-right">{{ report().report_1.month_current_name }}</th>
              <th class="text-right">% to total</th>
              <th class="text-right">{{ report().report_1.month_previous_name }}</th>

              <th class="text-right">
                % vs
                {{ report().report_1.month_previous_name }}
              </th>
              <th class="text-right">{{ report().report_1.month_current_last_year_name }}</th>
              <th class="text-right">YTD</th>
            </tr>
          </thead>
          <tbody>
            @for (location of report().locations; track location.title; let isLast = $last) {
              <tr>
                <td>{{ location.title }}</td>
                <td class="text-right">{{ location.totals?.current_month | number: '1.0-0' ?? '-' }}</td>
                <td class="text-right">{{ location.totals?.current_pct_of_total ?? '-' }}%</td>
                <td class="text-right">{{ location.totals?.previous_month | number: '1.0-0' ?? '-' }}</td>

                <td class="text-right">
                  @if (location.totals?.current_vs_previous_pct != null) {
                    {{ location.totals?.current_vs_previous_pct }}%
                  } @else {
                    -
                  }
                </td>
                <td class="text-right">{{ location.totals?.current_month_last_year ?? '-' }}</td>
                <td class="text-right">
                  {{ location.totals?.ytd | number: '1.0-0' ?? '-' }}
                </td>
              </tr>
              @if (isLast) {
                <tr class="totals">
                  <td></td>
                  <td class="text-right">{{ report().locationGrandTotals.current_month | number: '1.0-0' ?? '-' }}</td>
                  <td class="text-right"></td>
                  <td class="text-right">{{ report().locationGrandTotals.previous_month | number: '1.0-0' ?? '-' }}</td>

                  <td class="text-right"></td>
                  <td class="text-right">{{ report().locationGrandTotals.current_month_last_year | number: '1.0-0' ?? '-' }}</td>
                  <td class="text-right">
                    {{ report().locationGrandTotals.ytd | number: '1.0-0' ?? '-' }}
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </section>

      <!-- -->

      <section class="graph-1">
        <h1>Monthly Footfall Counts compared to temperature</h1>
        <div>
          <app-echart [telemetry]="report().report_4.footfall" telemetryLabel="Footfall" [weather]="report().report_4.temps" [height]="'400px'" chartType="bar" />
        </div>
      </section>

      <section class="graph-3">
        <h1>Monthly Footfall Counts - profile by hour</h1>
        <div>
          <app-insights-ff-lcr-hourly-chart [data]="report().report_5" />
        </div>
      </section>

      <section class="graph-4">
        <h1>Visitors by month</h1>
        <div>
          <app-insights-ff-lcr-monthly-chart [data]="report().report_6" />
        </div>
      </section>

      <section class="graph-5">
        <h1>Monthly Footfall Counts - rolling 12 months by day type</h1>
        <div>
          <app-insights-ff-lcr-rolling-year [data]="report().report_7" />
        </div>
      </section>
    </main>
    @if (showDebugging()) {
      <footer class="mt-1 dim no-print">
        <hr />
        <h3>Debugging</h3>
        <pre class="dim">{{ report() | json }}</pre>
      </footer>
    }
  } @placeholder {
    Generating report (polls every 5 seconds), please wait ( {{ isWaitingForResponse() * 5 }}s)... <i class="mdi mdi-spin-x2 mdi-loading"></i>
  }
</article>
